import type {
  SelectionnerOngletsIn,
  SelectionnerOngletsOut,
  TransitionCapAvenantSelectionnéIn,
} from './CreationDemandeConfiguration.types.ts'

import {
  type DemandeCloneDTO,
  type DemandeCreateDTO,
  type HabilitationDispositifDTO,
  type HabilitationsDTO,
  ONGLET_EMPLOYEUR,
} from '@/features/api/extranet/dto/Demande/shared'
import type { IdccDTO, LovItemDTO } from '@/features/api/extranet/dto/Lov'
import type { RattachementDTO } from '@/features/api/extranet/dto/Profil'
import type { EtablissementAdherentDTO } from '@/features/api/extranet/dto/shared'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import type {
  MachineEtatOptions,
  Runnable,
  Transition,
  TransitionOut,
  Transitions,
} from '@/features/domaine/machine-etats/'
import { type Dispositif, Interimaire, type NiveauPermission, type TypePublic } from '@/features/domaine/types/opco'
import type { TypeContratAvenant as TypeContratAvenantCAPP } from '@/features/domaine/types/opco/Demande/CAPP'
import type { TypeContratAvenant as TypeContratAvenantCPRO } from '@/features/domaine/types/opco/Demande/CPRO'
import { useMachineEtatDisplay } from '@/features/interface/machine-etats/useMachineEtat'
import allServices from '@/features/services/demande/DemandeAllService'
import { loadRattachements } from '@/features/services/profil/RattachementUtilisateurServices'

import type { RechercheQueryDossierReservationFondsDTO } from '@/features/api/extranet/dto/Dossier/Recherche/ReservationFonds/RechercheQueryDossierReservationFondsDTO.ts'
import type { RechercheResultDossierReservationFondsDTO } from '@/features/api/extranet/dto/Dossier/Recherche/ReservationFonds/RechercheResultDossierReservationFondsDTO.ts'
import { idccAutoriseTT, idccBudgetBloque, idccExclusAlerteEspaceFormation } from '@/features/domaine/regles-akto/shared'
import { SousDispositifDRF } from '@/features/domaine/types/opco/Demande/CXPI/SousDispositif.ts'
import { SousDispositif as SousDispositifOUTILFPE } from '@/features/domaine/types/opco/Demande/OUTILFPE/SousDispositif'
import ChoixDispositif from '@/features/interface/demande/ajouter/cartes/ChoixDispositif.vue'
import ChoixEtablissementModeAdherent from '@/features/interface/demande/ajouter/cartes/ChoixEtablissementModeAdherent.vue'
import ChoixEtablissementModePrestataire from '@/features/interface/demande/ajouter/cartes/ChoixEtablissementModePrestataire.vue'
import ChoixIdcc from '@/features/interface/demande/ajouter/cartes/ChoixIdcc.vue'
import ChoixMandatGestion from '@/features/interface/demande/ajouter/cartes/ChoixMandatGestion.vue'
import ChoixOnglets from '@/features/interface/demande/ajouter/cartes/ChoixOnglets.vue'
import ChoixRattachement from '@/features/interface/demande/ajouter/cartes/ChoixRattachement.vue'
import type {
  ChoixReservationFondsIn,
  ChoixReservationFondsOut,
} from '@/features/interface/demande/ajouter/cartes/ChoixReservationFonds.types.ts'
import ChoixReservationFonds from '@/features/interface/demande/ajouter/cartes/ChoixReservationFonds.vue'
import ChoixSousDispositif from '@/features/interface/demande/ajouter/cartes/ChoixSousDispositif.vue'
import ChoixTypeContratAvenant from '@/features/interface/demande/ajouter/cartes/ChoixTypeContratAvenant.vue'
import ChoixTypePublic from '@/features/interface/demande/ajouter/cartes/ChoixTypePublic.vue'
import { fabriqueRunCarte } from '@/features/interface/machine-etats/tools.ts'
import { EndPointsDrfDossierCxpi } from '@/features/services/locationToEndPoints.ts'
import { loadRecherche } from '@/features/services/recherche/RechercheService.ts'
import { isNotNullUndefinedOrEmptyString, isNullOrUndefined } from '@/features/shared/tools'
import type { PagedRequest } from '@/features/interface/recherches/shared'
import ChoixCapAvenant from './cartes/ChoixCapAvenant.vue'
import InformationAdherentNonEligible from './cartes/InformationAdherentNonEligible.vue'
import InformationEtablissementInactif from './cartes/InformationEtablissementInactif.vue'
import InformationFse from './cartes/InformationFse.vue'
import InformationEspaceFormation from './cartes/InformationEspaceFormation.vue'
import InformationIdccBudget from './cartes/InformationIdccBudget.vue'
import { SOUS_DISPOSITIFS_EXCLUES } from '@/features/domaine/constantes'

const ETAT_RATTACHEMENT_CHARGÉS = 'ETAT_RATTACHEMENT_CHARGÉS' as const
const transitionChargerRattachements: Runnable<void, RattachementDTO[]> = {
  run: async () => {
    const rattachements = await loadRattachements(true)
    return {
      out: rattachements ?? [],
      type: 'automatique',
    }
  },
}

const ETAT_RATTACHEMENT_SELECTIONNÉ = 'ETAT_RATTACHEMENT_SELECTIONNÉ' as const
const transitionSelectionnerRattachements: Runnable<RattachementDTO[], RattachementDTO> = {
  run: async (options) => {
    if (options.modelIn.length === 1) {
      return {
        out: options.modelIn[0]!,
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixRattachement, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<RattachementDTO>
  },
}

const ETAT_ETABLISSEMENT_SELECTIONNÉ = 'ETAT_ETABLISSEMENT_SELECTIONNÉ' as const
const transitionSelectionnerEtablissementModeAdherent: Runnable<RattachementDTO, EtablissementAdherentDTO> = {
  run: async (options) => {
    // console.log(options.modelIn.typeEntiteJuridique)
    if (options.modelIn.typeEntiteJuridique === 'Etablissement') {
      const etablissement = (
        await allServices.searchEtablissementAdherent(options.modelIn.identifiantEntiteJuridique)!
      ).at(0)!
      return {
        out: etablissement,
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixEtablissementModeAdherent, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<EtablissementAdherentDTO>
  },
}
const transitionSelectionnerEtablissementModePrestataire: Runnable<void, EtablissementAdherentDTO> = {
  run: fabriqueRunCarte(ChoixEtablissementModePrestataire),
}

const ETAT_IDCC_SELECTIONNÉ = 'ETAT_IDCC_SELECTIONNÉ' as const
const transitionSelectionnerIdcc: Runnable<EtablissementAdherentDTO, IdccDTO> = {
  run: async (options) => {
    if (options.modelIn.idcc) {
      return {
        out: options.modelIn.idcc,
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixIdcc, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<IdccDTO>
  },
}

const ETAT_HABILITATIONS_CHARGÉES = 'ETAT_HABILITATIONS_CHARGÉES' as const
export type TransitionChargementHabilitationsIn = { siret: string; idcc: string }
const transitionChargementHabilitations: Runnable<TransitionChargementHabilitationsIn, HabilitationsDTO> = {
  run: async (options) => {
    const habilitations = await allServices.getHabilitations('Formation', options.modelIn.siret, options.modelIn.idcc)
    return {
      out: habilitations,
      type: 'automatique',
    }
  },
}

const ETAT_TYPE_PUBLIC_SELECTIONNÉ = 'ETAT_TYPE_PUBLIC_SELECTIONNÉ' as const
const transitionSelectionnerTypePublic: Runnable<HabilitationsDTO, LovItemDTO<TypePublic>> = {
  run: async (options) => {
    // console.log(options.modelIn)
    if (options.modelIn.typePublics.length === 1) {
      return {
        out: options.modelIn.typePublics.at(0)!,
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixTypePublic, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}

const transitionFpeSelectionnerTypePublic: Runnable<HabilitationsDTO, LovItemDTO<TypePublic>> = {
  run: async (options) => {
    const typePublic = options.modelIn.typePublics.find((x) => x.code === 'Interimaire')
    if (typePublic != null) {
      return {
        out: typePublic,
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: InformationAdherentNonEligible, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}

const ETAT_DISPOSIF_SELECTIONNÉ = 'ETAT_DISPOSIF_SELECTIONNÉ' as const
const transitionSelectionnerDispositif: Runnable<HabilitationsDTO, HabilitationDispositifDTO> = {
  run: fabriqueRunCarte(ChoixDispositif),
}

const ETAT_SOUS_DISPOSITIF_SELECTIONNÉ = 'ETAT_SOUS_DISPOSITIF_SELECTIONNÉ' as const
const transitionSelectionnerSousDispositif: Runnable<HabilitationDispositifDTO, string | null> = {
  run: async (options) => {
    // console.log(options.modelIn.sousDispositifs)
    // Si clone sous dispositif (cipi/cdpi)
    if (['CDPI', 'CIPI', 'AfFpe', 'CAR'].includes(options.modelIn.sousDispositifClone!)) {
      const sousDispositifClone = options.modelIn.sousDispositifs.find((x) => x === options.modelIn.sousDispositifClone)
      if (sousDispositifClone) {
        return {
          out: sousDispositifClone,
          type: 'automatique',
        }
      } else {
        const display = useMachineEtatDisplay()
        const result = await display.runComponent({ component: InformationAdherentNonEligible, ...options })

        return {
          out: null,
          transitionState: result.transitionState,
          type: 'interactif',
        }
      }
    }

    if (!options.modelIn.sousDispositifs?.length) {
      return {
        out: null,
        type: 'automatique',
      }
    } else if (options.modelIn.sousDispositifs.length === 1) {
      return {
        out: options.modelIn.sousDispositifs[0]!,
        type: 'automatique',
      }
    }

    // dans le cas du FPE, on retire les sous dispositifs liés aux Outil FPE
    if (options.modelIn.code === 'FPE') {
      options.modelIn.sousDispositifs = options.modelIn.sousDispositifs.filter(
        (x) => SousDispositifOUTILFPE.includes(x as SousDispositifOUTILFPE) === false,
      )
    }

    options.modelIn.typeDemande = 'Formation'

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixSousDispositif, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}

const ETAT_CODE_DRF_SELECTIONNÉ = 'ETAT_CODE_DRF_SELECTIONNÉ' as const
const transitionSelectionnerCodeDRF: Runnable<ChoixReservationFondsIn, ChoixReservationFondsOut> = {
  run: async (options) => {
    const isClone = isNotNullUndefinedOrEmptyString(options.modelIn.codeDrf)

    // Charge les réservations de fonds accordées du siret ou du code DRF
    const query: RechercheQueryDossierReservationFondsDTO =
      isClone
        ? { search: [options.modelIn.codeDrf] }
        : {
            search: [options.modelIn.siret],
            statuts: ['Accorde', 'RelanceAvantCloture'],
          }

    const request: PagedRequest<RechercheQueryDossierReservationFondsDTO> = {
      query, orderBy: [], pagination: { pageSize: 500 },
    } as const

    const resultDrf = await loadRecherche<
      RechercheQueryDossierReservationFondsDTO,
      RechercheResultDossierReservationFondsDTO
    >(EndPointsDrfDossierCxpi.endPoints.endPointEntite, request, { dialogMode: 'retry' })

    const drfDossiers = resultDrf.data
      .filter((dossier) => dossier.sousDispositif === options.modelIn.sousDispositif)

    if (isClone && drfDossiers[0]?.niveauPermissionCxpi === 'Ecriture') {
      return {
        out: {
          code: drfDossiers[0].code,
          dispositif: drfDossiers[0].dispositif,
          sousDispositif: drfDossiers[0].sousDispositif,
        } as ChoixReservationFondsOut,
        type: 'automatique',
      }
    }
    options.modelIn.drfDossiers = drfDossiers
    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixReservationFonds, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}

const ETAT_TYPE_CONTRAT_AVENANT_SELECTIONNÉ = 'ETAT_TYPE_CONTRAT_AVENANT_SELECTIONNÉ' as const
const transitionSelectionnerTypeContratAvenant: Runnable<
  HabilitationsDTO,
  TypeContratAvenantCAPP | TypeContratAvenantCPRO
> = {
  run: fabriqueRunCarte(ChoixTypeContratAvenant),
}

const ETAT_MANDAT_GESTION_SELECTIONNÉ = 'ETAT_MANDAT_GESTION_SELECTIONNÉ' as const
const transitionSelectionnerMandatGestion: Runnable<void, boolean> = {
  run: fabriqueRunCarte(ChoixMandatGestion),
}

const ETAT_CAP_AVENANT_SELECTIONNÉ = 'ETAT_CAP_AVENANT_SELECTIONNÉ' as const
const transitionCapAvenantSelectionné: Runnable<TransitionCapAvenantSelectionnéIn, void> = {
  run: fabriqueRunCarte(ChoixCapAvenant),
}

const ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ = 'ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ' as const
const transitionEtablissementInactif: Runnable<EtablissementAdherentDTO, EtablissementAdherentDTO> = {
  run: async (options) => {
    if (options.modelIn.etatAdministratif === 'A') {
      return {
        out: options.modelIn,
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: InformationEtablissementInactif, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}

const ETAT_IDCC_BUDGET_SELECTIONNÉ = 'ETAT_IDCC_BUDGET_SELECTIONNÉ' as const
const transitionSelectionnerIdccBudget: Runnable<void, void> = {
  run: fabriqueRunCarte(InformationIdccBudget),
}

const ETAT_ESPACE_FORMATION_SELECTIONNÉ = 'ETAT_ESPACE_FORMATION_SELECTIONNÉ' as const
const transitionSelectionnerEspaceFormation: Runnable<void, void> = {
  run: fabriqueRunCarte(InformationEspaceFormation),
}

const ETAT_FSE_SELECTIONNÉ = 'ETAT_FSE_SELECTIONNÉ' as const
const transitionSelectionnerFse: Runnable<void, void> = {
  run: fabriqueRunCarte(InformationFse),
}

/* PRT-391 : retirer "consentement données personelles"
const ETAT_CONSENTEMENT_DONNEES_PERSONNELLES = 'ETAT_CONSENTEMENT_DONNEES_PERSONNELLES' as const
const transitionSelectionnerConsentementDonneesPersonnelles: Runnable<void, boolean> = {
  run: fabriqueRunCarte(ChoixConsentementDonnés)
}*/

/* Spécifiques clonnage */
const ETAT_HABILITATIONS_SOURCE_CHARGÉES = 'ETAT_HABILITATIONS_SOURCE_CHARGÉES' as const

const ETAT_ONGLETS_SELECTIONNÉS = 'ETAT_ONGLETS_SELECTIONNÉS' as const
const transitionSelectionnerOnglets: Runnable<SelectionnerOngletsIn, SelectionnerOngletsOut> = {
  run: fabriqueRunCarte(ChoixOnglets),
}

const transitionChargementEtablissementOnglet: Runnable<string, EtablissementAdherentDTO> = {
  run: async (options) => {
    const etablissement = (await allServices.searchEtablissementAdherent(options.modelIn)!).at(0)!
    return {
      out: etablissement,
      type: 'automatique',
    }
  },
}

type TransitionChargerHabilitationsDispositifIn = { habilitations: HabilitationsDTO; dispositif: Dispositif }
const transitionChargerHabilitationsDispositif: Runnable<
  TransitionChargerHabilitationsDispositifIn,
  HabilitationDispositifDTO
> = {
  run: (options) => {
    return {
      out: options.modelIn.habilitations.dispositifs.find((item) => item.code === options.modelIn.dispositif)!,
      type: 'automatique',
    }
  },
}

/* Transitions finales, communes à Creation et Clonnage */
const transitionsFinales: Transitions<void | CloneDemandeLaunch> = [
  {
    etatDepart: ETAT_TYPE_PUBLIC_SELECTIONNÉ,
    runnable: transitionSelectionnerDispositif,
    etatArrive: ETAT_DISPOSIF_SELECTIONNÉ,
    queryIn: ({ etats, contextCourant }) => {
      // Clean Habilitations
      const estAdhérent = contextCourant === TypesContextesAutorisesEnum.Adherent
      const estTT = idccAutoriseTT.includes(etats[ETAT_IDCC_SELECTIONNÉ].code)
      const estIntérimaire = etats[ETAT_TYPE_PUBLIC_SELECTIONNÉ].code === Interimaire

      const habilitations = etats[ETAT_HABILITATIONS_CHARGÉES] as HabilitationsDTO
      if (estAdhérent && estTT && estIntérimaire) {
        return habilitations
      } else {
        // Retire le dispositif CXPI
        const dispositifs = habilitations.dispositifs.filter((d) => d.code !== 'FPE')
        return {
          ...habilitations,
          dispositifs,
        }
      }
    },
  },
  {
    etatDepart: ETAT_DISPOSIF_SELECTIONNÉ,
    runnable: transitionSelectionnerSousDispositif,
    etatArrive: ETAT_SOUS_DISPOSITIF_SELECTIONNÉ,
    queryIn: ({ etats, launchParams }) => {
      const dispositif = { ...etats[ETAT_DISPOSIF_SELECTIONNÉ] } as HabilitationDispositifDTO

      // Exclue les sous-dispositifs non-autorisés
      if (SOUS_DISPOSITIFS_EXCLUES.length > 0) {
        dispositif.sousDispositifs = dispositif.sousDispositifs.filter(
          (sousDispositif) => !SOUS_DISPOSITIFS_EXCLUES.includes(sousDispositif),
        )
      }

      dispositif.sousDispositifClone = launchParams?.sousDispositif

      return dispositif
    },
    if: ({ etats }) => {
      return !['CAPP'].includes((etats[ETAT_DISPOSIF_SELECTIONNÉ] as HabilitationDispositifDTO).code)
    },
  },
  {
    etatDepart: ETAT_SOUS_DISPOSITIF_SELECTIONNÉ,
    runnable: transitionSelectionnerCodeDRF,
    etatArrive: ETAT_CODE_DRF_SELECTIONNÉ,
    queryIn: ({ etats, launchParams }) => {
      const etablissement = etats[ETAT_ETABLISSEMENT_SELECTIONNÉ] as EtablissementAdherentDTO
      const sousDispositif = launchParams?.sousDispositif
        ? launchParams?.sousDispositif
        : etats[ETAT_SOUS_DISPOSITIF_SELECTIONNÉ]
      return {
        siret: etablissement.siret.valeur,
        sousDispositif: sousDispositif,
        codeDrf: launchParams?.codeDrf,
      } as ChoixReservationFondsIn
    },
    if: ({ etats }) => {
      return SousDispositifDRF.includes(etats[ETAT_SOUS_DISPOSITIF_SELECTIONNÉ] as SousDispositifDRF)
    },
  },
  {
    etatDepart: [ETAT_DISPOSIF_SELECTIONNÉ, ETAT_SOUS_DISPOSITIF_SELECTIONNÉ],
    runnable: transitionSelectionnerTypeContratAvenant,
    etatArrive: ETAT_TYPE_CONTRAT_AVENANT_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_DISPOSIF_SELECTIONNÉ] as HabilitationDispositifDTO
    },
    if: ({ etats }) => {
      return ['CAPP', 'CPRO'].includes(etats[ETAT_DISPOSIF_SELECTIONNÉ].code)
    },
  },
  {
    etatDepart: [ETAT_DISPOSIF_SELECTIONNÉ, ETAT_SOUS_DISPOSITIF_SELECTIONNÉ, ETAT_TYPE_CONTRAT_AVENANT_SELECTIONNÉ],
    runnable: transitionSelectionnerMandatGestion,
    etatArrive: ETAT_MANDAT_GESTION_SELECTIONNÉ,
    if: ({ etats, contextCourant }) => {
      return (
        contextCourant === TypesContextesAutorisesEnum.Prestataire && etats[ETAT_DISPOSIF_SELECTIONNÉ].code === 'CAPP'
      )
    },
  },
  {
    etatDepart: [ETAT_SOUS_DISPOSITIF_SELECTIONNÉ],
    runnable: transitionSelectionnerIdccBudget,
    etatArrive: ETAT_IDCC_BUDGET_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_IDCC_SELECTIONNÉ] as IdccDTO
    },
    if: ({ etats }) =>
      etats[ETAT_SOUS_DISPOSITIF_SELECTIONNÉ] === 'PDC' &&
      idccBudgetBloque.includes(etats[ETAT_IDCC_SELECTIONNÉ]!.code),
  },
  {
    etatDepart: [ETAT_SOUS_DISPOSITIF_SELECTIONNÉ],
    runnable: transitionSelectionnerEspaceFormation,
    etatArrive: ETAT_ESPACE_FORMATION_SELECTIONNÉ,
    if: ({ etats }) => {
      if (etats[ETAT_SOUS_DISPOSITIF_SELECTIONNÉ] !== 'PDC') return false
      const idcc = etats[ETAT_IDCC_SELECTIONNÉ] as IdccDTO
      if (idccExclusAlerteEspaceFormation.includes(idcc.code)) return false
      const etablissement = etats[ETAT_ETABLISSEMENT_SELECTIONNÉ] as EtablissementAdherentDTO
      const effectif = etablissement.dernierEffectifConnu
      return !effectif || effectif < 50
    },
  },
  {
    etatDepart: [ETAT_SOUS_DISPOSITIF_SELECTIONNÉ],
    runnable: transitionSelectionnerFse,
    etatArrive: ETAT_FSE_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_IDCC_SELECTIONNÉ] as IdccDTO
    },
    // PRT-1302, RÉOUVERTURE JUSQU'AU 29/04/2026
    if: () => {
      return false
    },
    /*
    if: ({ etats }) => {
      return etats[ETAT_SOUS_DISPOSITIF_SELECTIONNÉ] === 'Fse'
    },
    */
  },
  {
    etatDepart: [ETAT_TYPE_CONTRAT_AVENANT_SELECTIONNÉ, ETAT_MANDAT_GESTION_SELECTIONNÉ],
    runnable: transitionCapAvenantSelectionné,
    etatArrive: ETAT_CAP_AVENANT_SELECTIONNÉ,
    if: ({ etats, contextCourant }) => {
      if (etats[ETAT_DISPOSIF_SELECTIONNÉ].code !== 'CAPP') return false
      if (
        contextCourant === TypesContextesAutorisesEnum.Prestataire &&
        isNullOrUndefined(etats[ETAT_MANDAT_GESTION_SELECTIONNÉ])
      )
        return false
      return [
        'ProlongationSuiteEchecExamen',
        'ProlongationSuiteReconnaissanceHandicap',
        'DiplomeSupplementaireApprenti',
        'AutresChangements',
        'ModificationLieuExecutionContrat',
        'ModificationLieuFormationTheorique',
      ].includes(etats[ETAT_TYPE_CONTRAT_AVENANT_SELECTIONNÉ])
    },
    queryIn: ({ etats, contextCourant }) => {
      if (contextCourant === TypesContextesAutorisesEnum.Prestataire)
        return {
          context: TypesContextesAutorisesEnum.Prestataire,
          sousMandatGestion: etats[ETAT_MANDAT_GESTION_SELECTIONNÉ] as boolean,
        }
      else
        return {
          context: TypesContextesAutorisesEnum.Adherent,
        }
    },
  } as Transition<TransitionCapAvenantSelectionnéIn, void, void | CloneDemandeLaunch>,
  /* PRT-391 : retirer "consentement données personelles"
  ,{
    etatDepart: [ETAT_DISPOSIF_SELECTIONNÉ, ETAT_SOUS_DISPOSITIF_SELECTIONNÉ, ETAT_TYPE_CONTRAT_AVENANT_SELECTIONNÉ, ETAT_MANDAT_GESTION_SELECTIONNÉ],
    runnable: transitionSelectionnerConsentementDonneesPersonnelles,
    etatArrive: ETAT_CONSENTEMENT_DONNEES_PERSONNELLES
  }
  */
]

const transitionsCreation: Transitions<void> = [
  /* Pour l'adhérent le process démarre ici */
  {
    etatDepart: null,
    runnable: transitionChargerRattachements,
    etatArrive: ETAT_RATTACHEMENT_CHARGÉS,
    if: ({ contextCourant }) => contextCourant === TypesContextesAutorisesEnum.Adherent,
  },
  {
    etatDepart: ETAT_RATTACHEMENT_CHARGÉS,
    runnable: transitionSelectionnerRattachements,
    etatArrive: ETAT_RATTACHEMENT_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_RATTACHEMENT_CHARGÉS]
    },
  },
  {
    etatDepart: ETAT_RATTACHEMENT_SELECTIONNÉ,
    runnable: transitionSelectionnerEtablissementModeAdherent,
    etatArrive: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_RATTACHEMENT_SELECTIONNÉ]
    },
  },
  /* Pour les prestataires le process démarre ici */
  {
    etatDepart: null,
    runnable: transitionSelectionnerEtablissementModePrestataire,
    etatArrive: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    if: ({ contextCourant }) => contextCourant === TypesContextesAutorisesEnum.Prestataire,
  },
  {
    etatDepart: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    runnable: transitionEtablissementInactif,
    etatArrive: ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_ETABLISSEMENT_SELECTIONNÉ]
    },
  },
  {
    etatDepart: ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ,
    runnable: transitionSelectionnerIdcc,
    etatArrive: ETAT_IDCC_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_ETABLISSEMENT_SELECTIONNÉ]
    },
  },
  {
    etatDepart: ETAT_IDCC_SELECTIONNÉ,
    runnable: transitionChargementHabilitations,
    etatArrive: ETAT_HABILITATIONS_CHARGÉES,
    queryIn: ({ etats }) => {
      return {
        siret: (etats[ETAT_ETABLISSEMENT_SELECTIONNÉ] as EtablissementAdherentDTO).siret.valeur,
        idcc: (etats[ETAT_IDCC_SELECTIONNÉ] as IdccDTO).code,
      }
    },
  } as Transition<TransitionChargementHabilitationsIn, HabilitationsDTO>,
  {
    etatDepart: ETAT_HABILITATIONS_CHARGÉES,
    runnable: transitionSelectionnerTypePublic,
    etatArrive: ETAT_TYPE_PUBLIC_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_HABILITATIONS_CHARGÉES] as HabilitationsDTO
    },
  },
  ...transitionsFinales,
] as const

export const CreationDemandeConfigurations: MachineEtatOptions<void, DemandeCreateDTO> = {
  transitions: transitionsCreation,
  resultCallback: ({ etats }) => {
    const sousDispositif =
      (etats[ETAT_SOUS_DISPOSITIF_SELECTIONNÉ] as string) ??
      (etats[ETAT_CODE_DRF_SELECTIONNÉ] as ChoixReservationFondsOut)?.sousDispositif

    return {
      typeDemande: 'Formation',
      dispositif: (etats[ETAT_DISPOSIF_SELECTIONNÉ] as HabilitationDispositifDTO).code,
      sousDispositif: sousDispositif,
      codeDrf: (etats[ETAT_CODE_DRF_SELECTIONNÉ] as ChoixReservationFondsOut)?.code,
      siret: (etats[ETAT_ETABLISSEMENT_SELECTIONNÉ] as EtablissementAdherentDTO).siret.valeur!,
      idcc: (etats[ETAT_IDCC_SELECTIONNÉ] as IdccDTO).code,
      typePublic: (etats[ETAT_TYPE_PUBLIC_SELECTIONNÉ] as LovItemDTO<TypePublic>).code,
      typeContratAvenant: etats[ETAT_TYPE_CONTRAT_AVENANT_SELECTIONNÉ] as
        | TypeContratAvenantCAPP
        | TypeContratAvenantCPRO,
      consentementTraitementDonnees: true,
      estSousMandatGestion: etats[ETAT_MANDAT_GESTION_SELECTIONNÉ],
    } as DemandeCreateDTO
  },
  disposeCallback: () => useMachineEtatDisplay().hide(),
}

export type CloneDemandeLaunch = {
  idDemande: number
  dispositif: Dispositif
  siret: string
  idcc: string
  permissions: NiveauPermission
  sousDispositif: string
  codeDrf: string | null
}
const transitionsClone: Transitions<CloneDemandeLaunch> = [
  /* Lancement process adhérent */
  {
    etatDepart: null,
    runnable: transitionChargementHabilitations,
    etatArrive: ETAT_HABILITATIONS_SOURCE_CHARGÉES,
    queryIn: ({ launchParams }) => {
      return {
        siret: launchParams!.siret,
        idcc: launchParams!.idcc,
      }
    },
    if: ({ contextCourant }) => contextCourant === TypesContextesAutorisesEnum.Adherent,
  } as Transition<TransitionChargementHabilitationsIn, HabilitationsDTO, CloneDemandeLaunch>,
  /* Lancement process prestataire */
  {
    etatDepart: [ETAT_HABILITATIONS_SOURCE_CHARGÉES, null],
    runnable: transitionSelectionnerOnglets,
    etatArrive: ETAT_ONGLETS_SELECTIONNÉS,
    queryIn: ({ launchParams, contextCourant, etats }) => {
      return {
        typeDemande: 'Formation',
        context: contextCourant,
        dispositif: launchParams?.dispositif,
        sousDispositif: launchParams?.sousDispositif,
        habilitations: etats[ETAT_HABILITATIONS_SOURCE_CHARGÉES],
        permissions: launchParams?.permissions,
      }
    },
  } as Transition<SelectionnerOngletsIn, SelectionnerOngletsOut, CloneDemandeLaunch>,
  /* Selection etablissement mode Adhérent */
  {
    etatDepart: ETAT_ONGLETS_SELECTIONNÉS,
    runnable: transitionChargerRattachements,
    etatArrive: ETAT_RATTACHEMENT_CHARGÉS,
    if: ({ contextCourant, etats }) => {
      if (contextCourant === TypesContextesAutorisesEnum.Prestataire) return false
      return (etats[ETAT_ONGLETS_SELECTIONNÉS] as string[]).includes(ONGLET_EMPLOYEUR) === false
    },
  } as Transition<void, RattachementDTO[], CloneDemandeLaunch>,
  {
    etatDepart: ETAT_RATTACHEMENT_CHARGÉS,
    runnable: transitionSelectionnerRattachements,
    etatArrive: ETAT_RATTACHEMENT_SELECTIONNÉ,
    queryIn: ({ etats, launchParams }) => {
      const rattachements: RattachementDTO[] = etats[ETAT_RATTACHEMENT_CHARGÉS]
      return rattachements.filter((item) =>
        item.permissions.some(
          (permission) => permission.type === launchParams?.dispositif && permission.niveau === 'Ecriture',
        ),
      )
    },
  } as Transition<RattachementDTO[], RattachementDTO, CloneDemandeLaunch>,
  {
    etatDepart: ETAT_RATTACHEMENT_SELECTIONNÉ,
    runnable: transitionSelectionnerEtablissementModeAdherent,
    etatArrive: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    queryIn: ({ etats }) => etats[ETAT_RATTACHEMENT_SELECTIONNÉ],
  } as Transition<RattachementDTO, EtablissementAdherentDTO, CloneDemandeLaunch>,
  /* Selection etablissement mode Prestataire */
  {
    etatDepart: ETAT_ONGLETS_SELECTIONNÉS,
    runnable: transitionSelectionnerEtablissementModePrestataire,
    etatArrive: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    if: ({ etats, contextCourant }) => {
      if (contextCourant === TypesContextesAutorisesEnum.Adherent) return false
      return (etats[ETAT_ONGLETS_SELECTIONNÉS] as string[]).includes(ONGLET_EMPLOYEUR) === false
    },
  } as Transition<void, EtablissementAdherentDTO, CloneDemandeLaunch>,
  {
    etatDepart: ETAT_ONGLETS_SELECTIONNÉS,
    runnable: transitionChargementEtablissementOnglet,
    etatArrive: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    queryIn: ({ launchParams }) => launchParams?.siret,
    if: ({ etats }) => {
      return (etats[ETAT_ONGLETS_SELECTIONNÉS] as string[]).includes(ONGLET_EMPLOYEUR)
    },
  } as Transition<string, EtablissementAdherentDTO, CloneDemandeLaunch>,
  {
    etatDepart: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    runnable: transitionEtablissementInactif,
    etatArrive: ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_ETABLISSEMENT_SELECTIONNÉ]
    },
  },
  {
    etatDepart: ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ,
    runnable: transitionSelectionnerIdcc,
    etatArrive: ETAT_IDCC_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_ETABLISSEMENT_SELECTIONNÉ]
    },
  },
  {
    etatDepart: ETAT_IDCC_SELECTIONNÉ,
    runnable: transitionChargementHabilitations,
    etatArrive: ETAT_HABILITATIONS_CHARGÉES,
    queryIn: ({ etats }) => {
      return {
        siret: (etats[ETAT_ETABLISSEMENT_SELECTIONNÉ] as EtablissementAdherentDTO).siret.valeur,
        idcc: (etats[ETAT_IDCC_SELECTIONNÉ] as IdccDTO).code,
      }
    },
  } as Transition<TransitionChargementHabilitationsIn, HabilitationsDTO>,
  {
    etatDepart: ETAT_HABILITATIONS_CHARGÉES,
    runnable: transitionSelectionnerTypePublic,
    etatArrive: ETAT_TYPE_PUBLIC_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_HABILITATIONS_CHARGÉES] as HabilitationsDTO
    },
    if: ({ launchParams }) => {
      return launchParams?.dispositif !== 'FPE'
    },
  },
  {
    etatDepart: ETAT_HABILITATIONS_CHARGÉES,
    runnable: transitionFpeSelectionnerTypePublic,
    etatArrive: ETAT_TYPE_PUBLIC_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_HABILITATIONS_CHARGÉES] as HabilitationsDTO
    },
    if: ({ launchParams }) => {
      return launchParams?.dispositif === 'FPE'
    },
  },

  {
    etatDepart: ETAT_TYPE_PUBLIC_SELECTIONNÉ,
    runnable: transitionChargerHabilitationsDispositif,
    etatArrive: ETAT_DISPOSIF_SELECTIONNÉ,
    queryIn: ({ etats, launchParams }) => {
      return {
        habilitations: etats[ETAT_HABILITATIONS_CHARGÉES] as HabilitationsDTO,
        dispositif: launchParams?.dispositif,
      }
    },
  } as Transition<TransitionChargerHabilitationsDispositifIn, HabilitationDispositifDTO, CloneDemandeLaunch>,
  ...transitionsFinales,
]

export const CloneDemandeConfiguration: MachineEtatOptions<CloneDemandeLaunch, DemandeCloneDTO> = {
  transitions: transitionsClone,
  resultCallback: ({ etats, launchParams }) => {
    return {
      typeDemande: 'Formation',
      id: launchParams?.idDemande,
      dispositif: (etats[ETAT_DISPOSIF_SELECTIONNÉ] as HabilitationDispositifDTO).code,
      sousDispositif: (etats[ETAT_SOUS_DISPOSITIF_SELECTIONNÉ] as string | null) ?? undefined,
      siret: (etats[ETAT_ETABLISSEMENT_SELECTIONNÉ] as EtablissementAdherentDTO).siret.valeur!,
      idcc: (etats[ETAT_IDCC_SELECTIONNÉ] as IdccDTO).code,
      typePublic: (etats[ETAT_TYPE_PUBLIC_SELECTIONNÉ] as LovItemDTO<TypePublic>).code,
      typeContratAvenant: etats[ETAT_TYPE_CONTRAT_AVENANT_SELECTIONNÉ] as
        | TypeContratAvenantCAPP
        | TypeContratAvenantCPRO,
      consentementTraitementDonnees: true,
      estSousMandatGestion: etats[ETAT_MANDAT_GESTION_SELECTIONNÉ],
      onglets: etats[ETAT_ONGLETS_SELECTIONNÉS],
      codeDrf: (etats[ETAT_CODE_DRF_SELECTIONNÉ] as ChoixReservationFondsOut)?.code,
    } as DemandeCloneDTO
  },
  disposeCallback: () => useMachineEtatDisplay().hide(),
}
